/**
 * TheNewsPulse — Community submission form.
 * Route: /contribute/new (auth-gated on the backend + soft-gated here).
 *
 * Flow: location → category → headline → story → photos → source links →
 *       event date/time → submit for review.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { npApi } from "./api";
import RichTextEditor from "@/components/newspulse/RichTextEditor";
import LocationAutocomplete from "@/components/newspulse/LocationAutocomplete";
import TransliterationBar from "@/components/newspulse/TransliterationBar";
import {
  MapPin, ChevronRight, Camera, Link2, Calendar, Loader2, X,
  AlertCircle, CheckCircle2, ShieldCheck, ArrowLeft,
} from "lucide-react";

function Field({ label, hint, required, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-1.5">
        {label} {required && <span className="text-rose-600">*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </label>
  );
}

const inputCls =
  "w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:border-blue-500 focus:outline-none";

export default function NewsPulseContributeSubmit() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth() || {};

  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    headline: "",
    story: "",
    story_html: "",
    lang: "en",
    category: "",
    city: "", state: "", country: "", neighborhood: "",
    event_datetime: "",
    firsthand: true,
    photo_urls: [],
    source_links: ["", ""],
  });
  const [lastWord, setLastWord] = useState("");
  const headlineRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);

  useEffect(() => {
    npApi.contribCategories().then((d) => setCats(d.categories || [])).catch(() => {});
  }, []);

  const isSignedIn = Boolean(user && !authLoading);
  const canSubmit = useMemo(() => {
    return (
      form.headline.trim().length >= 8 &&
      form.story.trim().length >= 40 &&
      form.category &&
      (form.city.trim() || form.neighborhood.trim() || form.country.trim())
    );
  }, [form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onPhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (form.photo_urls.length >= 4) { setErr("Max 4 photos"); return; }
    if (file.size > 8 * 1024 * 1024) { setErr("Photo exceeds 8 MB"); return; }
    setUploading(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const t = localStorage.getItem("sp_token") || localStorage.getItem("token") || "";
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || ""}/api/newspulse/v1/contribute/upload-photo`,
        { method: "POST", body: fd, headers: t ? { Authorization: `Bearer ${t}` } : {} },
      );
      if (!r.ok) throw new Error((await r.text()).slice(0, 200));
      const d = await r.json();
      setForm((f) => ({ ...f, photo_urls: [...f.photo_urls, d.url] }));
    } catch (e) {
      setErr(`Photo upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!isSignedIn) { nav(`/auth?next=${encodeURIComponent("/contribute/new")}`); return; }
    setSaving(true); setErr(null); setOk(null);
    try {
      const payload = {
        headline: form.headline.trim(),
        story: form.story.trim(),
        story_html: form.story_html || null,
        lang: form.lang || "en",
        category: form.category,
        location: {
          city: form.city.trim() || null,
          state: form.state.trim() || null,
          country: form.country.trim() || null,
          neighborhood: form.neighborhood.trim() || null,
        },
        event_datetime: form.event_datetime || null,
        photo_urls: form.photo_urls,
        source_links: form.source_links.filter((s) => s.trim()),
        firsthand: form.firsthand,
      };
      const res = await npApi.contribSubmit(payload);
      setOk(res.id);
      setTimeout(() => nav("/contribute/mine"), 1200);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  if (authLoading) {
    return <div className="p-12 text-center text-slate-500" data-testid="contribute-loading">Loading…</div>;
  }
  if (!isSignedIn) {
    return (
      <div className="max-w-[720px] mx-auto px-4 py-16 text-center" data-testid="contribute-signin-wall">
        <ShieldCheck className="w-10 h-10 text-blue-600 mx-auto mb-4" />
        <h1 className="text-lg md:text-lg font-bold text-slate-900 mb-2">Sign in to contribute</h1>
        <p className="text-sm text-slate-600 mb-6">
          Community reporting on TheNewsPulse.pro is limited to signed-in
          SocialPulse users, so we can attribute stories and build a
          responsible network of local voices.
        </p>
        <Link
          to={`/auth?next=${encodeURIComponent("/contribute/new")}`}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-full transition-colors"
          data-testid="contribute-signin-btn"
        >
          Sign in with SocialPulse SSO <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[820px] mx-auto px-4 lg:px-6 py-10" data-testid="contribute-submit-page">
      <Link to="/contribute" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4"
            data-testid="contribute-back">
        <ArrowLeft className="w-4 h-4" /> Back to Contributor guide
      </Link>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-widest font-bold text-blue-700 mb-2">
          Report a Community Story
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900"
            style={{ fontFamily: "'Lora', 'Georgia', serif" }}>
          Tell your community.
        </h1>
        <p className="text-sm text-slate-600 mt-3 max-w-xl">
          Share information you reasonably believe to be accurate. Firsthand
          observations, photos, and source links make your story more useful
          and trustworthy.
        </p>
      </div>

      {ok && (
        <div className="mb-6 rounded-xl border border-green-200 bg-green-50 px-4 py-3 flex gap-2 text-sm text-green-900"
             data-testid="contribute-success">
          <CheckCircle2 className="w-4 h-4 mt-0.5" />
          Thanks — your submission is queued for editorial review (ID {ok}). Redirecting…
        </div>
      )}
      {err && (
        <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex gap-2 text-sm text-rose-900"
             data-testid="contribute-error">
          <AlertCircle className="w-4 h-4 mt-0.5" /> {err}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6" data-testid="contribute-form">
        {/* Location */}
        <div className="rounded-2xl border border-slate-200 p-5 bg-white">
          <div className="flex items-center gap-2 mb-3 text-slate-900 font-bold">
            <MapPin className="w-4 h-4 text-blue-600" /> Location
          </div>
          <div className="mb-3">
            <Field label="Search a place" hint="Pick a suggestion to auto-fill the fields below."
                   testId="contribute-field-loc-search">
              <LocationAutocomplete
                value={form.city}
                onChange={(v) => set("city", v)}
                onSelect={(picked) => setForm((f) => ({
                  ...f,
                  city:    picked.city    || f.city,
                  state:   picked.state   || f.state,
                  country: picked.country || f.country,
                }))}
                testId="contribute-loc-autocomplete"
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="City / Town" testId="contribute-field-city">
              <input className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)}
                     placeholder="e.g. Kochi" data-testid="contribute-input-city" />
            </Field>
            <Field label="Neighborhood / Village" testId="contribute-field-neighborhood">
              <input className={inputCls} value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)}
                     placeholder="e.g. Kadanad" data-testid="contribute-input-neighborhood" />
            </Field>
            <Field label="State / Province" testId="contribute-field-state">
              <input className={inputCls} value={form.state} onChange={(e) => set("state", e.target.value)}
                     placeholder="e.g. Kerala" data-testid="contribute-input-state" />
            </Field>
            <Field label="Country" required testId="contribute-field-country">
              <input className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)}
                     placeholder="e.g. India" data-testid="contribute-input-country" />
            </Field>
          </div>
        </div>

        {/* Category */}
        <Field label="Category" required testId="contribute-field-category" hint="Pick the closest fit — an editor can retag if needed.">
          <select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}
                  data-testid="contribute-input-category">
            <option value="">Choose a category…</option>
            {cats.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
          </select>
        </Field>

        {/* Headline */}
        <Field label="Headline" required
               hint={`${form.headline.length}/180`}
               testId="contribute-field-headline">
          <input className={inputCls} value={form.headline} maxLength={180} ref={headlineRef}
                 onChange={(e) => {
                   set("headline", e.target.value);
                   const w = e.target.value.trim().split(/\s+/).pop() || "";
                   setLastWord(w);
                 }}
                 placeholder="A short, factual summary readers will see first"
                 data-testid="contribute-input-headline" />
        </Field>

        {/* Transliteration + language */}
        <TransliterationBar
          lang={form.lang}
          onChangeLang={(lg) => set("lang", lg)}
          latestWord={lastWord}
          onPick={(native) => {
            // Replace the last word in the headline field with the native pick.
            const words = form.headline.trim().split(/\s+/);
            words[words.length - 1] = native;
            const next = words.join(" ") + " ";
            setForm((f) => ({ ...f, headline: next }));
            setLastWord("");
            headlineRef.current?.focus();
          }}
        />

        {/* Story — rich text editor */}
        <Field label="Story" required
               hint="Firsthand? Say so. Second-hand? Say who told you. Use the toolbar for fonts, headings, images, links and video."
               testId="contribute-field-story">
          <RichTextEditor
            value={form.story_html}
            onChange={(html) => setForm((f) => ({
              ...f,
              story_html: html,
              // keep a plain-text mirror for search + validation
              story: html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(),
            }))}
            placeholder="What happened? Where, when, who, and why does it matter to your community?"
            onUploadImage={async (file) => {
              const fd = new FormData();
              fd.append("file", file);
              const t = localStorage.getItem("sp_token") || localStorage.getItem("token") || "";
              const r = await fetch(
                `${process.env.REACT_APP_BACKEND_URL || ""}/api/newspulse/v1/contribute/upload-photo`,
                { method: "POST", body: fd, headers: t ? { Authorization: `Bearer ${t}` } : {} },
              );
              if (!r.ok) throw new Error("upload failed");
              const d = await r.json();
              return d.url;
            }}
            testId="contribute-rte"
          />
          <div className="text-[11px] text-slate-500 mt-1">{form.story.length}/8000 characters</div>
        </Field>

        {/* Firsthand toggle */}
        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer" data-testid="contribute-firsthand-wrap">
          <input type="checkbox" checked={form.firsthand}
                 onChange={(e) => set("firsthand", e.target.checked)}
                 className="mt-1 w-4 h-4 accent-blue-600"
                 data-testid="contribute-input-firsthand" />
          <span>
            <strong>I witnessed this myself</strong> or spoke directly to people
            involved. Uncheck if you received this information second-hand.
          </span>
        </label>

        {/* Photos */}
        <div className="rounded-2xl border border-slate-200 p-5 bg-white">
          <div className="flex items-center gap-2 mb-3 text-slate-900 font-bold">
            <Camera className="w-4 h-4 text-blue-600" /> Photos (optional · max 4)
          </div>
          <div className="flex flex-wrap gap-3 mb-3" data-testid="contribute-photo-thumbs">
            {form.photo_urls.map((u, i) => (
              <div key={u + i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-slate-200">
                <img src={u} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
                <button type="button"
                        onClick={() => setForm((f) => ({ ...f, photo_urls: f.photo_urls.filter((_, j) => j !== i) }))}
                        className="absolute top-1 right-1 bg-slate-900/80 text-white rounded-full w-6 h-6 flex items-center justify-center"
                        aria-label="Remove photo"
                        data-testid={`contribute-photo-remove-${i}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {form.photo_urls.length < 4 && (
              <label className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 text-slate-500 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:text-blue-600 transition-colors"
                     data-testid="contribute-photo-picker">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                <span className="text-[10px] mt-1">{uploading ? "Uploading…" : "Add photo"}</span>
                <input type="file" accept="image/*" onChange={onPhoto} className="hidden"
                       disabled={uploading} data-testid="contribute-photo-input" />
              </label>
            )}
          </div>
          <div className="text-[11px] text-slate-500">
            JPEG/PNG/WebP/GIF up to 8 MB. Respect privacy — blur faces or plates if in doubt.
          </div>
        </div>

        {/* Source links */}
        <Field label="Source links (optional · https only)" testId="contribute-field-sources"
               hint="Add up to 6 links to news articles, gov notices, or official announcements that back up your report.">
          <div className="space-y-2" data-testid="contribute-source-wrap">
            {form.source_links.map((s, i) => (
              <div key={i} className="flex gap-2">
                <Link2 className="w-4 h-4 text-slate-400 mt-2.5 shrink-0" />
                <input className={inputCls} value={s}
                       onChange={(e) => setForm((f) => {
                         const copy = [...f.source_links]; copy[i] = e.target.value; return { ...f, source_links: copy };
                       })}
                       placeholder="https://…"
                       data-testid={`contribute-input-source-${i}`} />
              </div>
            ))}
            {form.source_links.length < 6 && (
              <button type="button" onClick={() => setForm((f) => ({ ...f, source_links: [...f.source_links, ""] }))}
                      className="text-sm text-blue-600 hover:text-blue-800"
                      data-testid="contribute-source-add">
                + Add another link
              </button>
            )}
          </div>
        </Field>

        {/* Event date */}
        <Field label="When did this happen?" testId="contribute-field-datetime"
               hint="Approximate is fine. Use your local timezone.">
          <div className="flex gap-2 items-start">
            <Calendar className="w-4 h-4 text-slate-400 mt-2.5 shrink-0" />
            <input type="datetime-local" className={inputCls}
                   value={form.event_datetime} onChange={(e) => set("event_datetime", e.target.value)}
                   data-testid="contribute-input-datetime" />
          </div>
        </Field>

        {/* Submit */}
        <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
          <button type="submit" disabled={!canSubmit || saving}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-full transition-colors"
                  data-testid="contribute-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {saving ? "Submitting…" : "Submit for editorial review"}
          </button>
          <span className="text-[11px] text-slate-500">
            An editor will review before publishing. You'll see the status in <Link to="/contribute/mine" className="underline">My Contributions</Link>.
          </span>
        </div>
      </form>
    </div>
  );
}
